import React, { useState } from 'react';
import ReactSelect from 'react-select';
import { Col, Row } from 'react-flexbox-grid';
import { Blockquote, Modal, RadioButton } from '@wfp/ui';
import CreatableSelect from 'react-select/creatable';

import { type  Edition, type  EmailRecipients, type SimpleOption } from '../../restapi/types';
import { formatDateLong } from '../../utils';

import './PublishModal.scss';
import { editions } from '../../restapi/document';


interface Props {
	edition: Edition;
	done: (data?: EmailRecipients, when?: string) => void,
}

// RFC 2822
// eslint-disable-next-line no-control-regex
const mailRE = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
const endWithLetter = /[a-z]$/;

function checkRecipients(data: EmailRecipients) {
	if (data.to.length + data.cc.length + data.bcc.length === 0) {
		return ['Provide at least one email address.'];
	}

	const errors: string[] = [];
	const fields: (keyof EmailRecipients)[] = ['to', 'cc', 'bcc'];
	fields.forEach(field =>
		data[field].forEach(e => {
			if (!e.toLowerCase().match(mailRE) || !e.toLowerCase().match(endWithLetter)) {
				errors.push(`Invalid ${field} recipient: ${e}`);
			}
		}),
	);

	return errors;
}

const whenHourOptions = [...Array(24)].map(
	(_, h) => ({value: h, label: h === 0 ? '12 am' : h === 12 ? '12 pm' : (h % 12) + (h < 12 ? ' am' : ' pm')}),
);

const whenMinutesOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(
	i => ({value: i, label: (i < 10 ? '0' : '') + i}),
);

function packWhen(date: string, h: number, m: number) {
	return `${date} ${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}:00`;
}

export function PublishModal({edition: ed, done}: Props) {
	const [to, setTo] = useState((ed.addresses?.to || []).map(a => ({value: a, label: a})));
	const [cc, setCc] = useState((ed.addresses?.cc || []).map(a => ({value: a, label: a})));
	const [bcc, setBcc] = useState((ed.addresses?.bcc || []).map(a => ({value: a, label: a})));
	const [whenActive, setWhenActive] = useState(true);
	const [whenHour, setWhenHour] = useState(8);
	const [whenMinutes, setWhenMinutes] = useState(0);
	const [errors, setErrors] = useState<string[]>([]);

	return (
		<Modal
			open wide
			modalHeading="Publish edition and send email"
			primaryButtonText={whenActive ? "Publish now and send later" : "Publish and send"}
			secondaryButtonText="Cancel"
			onRequestSubmit={() => {  // primary button close
				const recipients = {
					to: to.map(o => o.value),
					cc: cc.map(o => o.value),
					bcc: bcc.map(o => o.value),
				};
				const when = whenActive ? packWhen(ed.docSlug, whenHour, whenMinutes) : undefined;
				const errors = checkRecipients(recipients);
				if (errors.length) {
					setErrors(errors);
				} else {
					done(recipients, when);
				}
			}}
			onRequestClose={() => done()}>

			<table className="publish"><tbody>
				<tr>
					<th>To</th>
					<td className="no-padding">
						<CreatableSelect
							id="address-to"
							className="wfp--react-select-container"
							classNamePrefix="wfp--react-select"
							isMulti
							isClearable
							options={[]}
							value={to}
							onChange={v => setTo(v as SimpleOption[])}
						/>
					</td>
				</tr>
				<tr>
					<th>Cc</th>
					<td className="no-padding">
						<CreatableSelect
							id="address-cc"
							className="wfp--react-select-container"
							classNamePrefix="wfp--react-select"
							isMulti
							isClearable
							options={[]}
							value={cc}
							onChange={v => setCc(v as SimpleOption[])}
						/>
					</td>
				</tr>
				<tr>
					<th>Bcc</th>
					<td className="no-padding">
						<CreatableSelect
							id="address-bcc"
							className="wfp--react-select-container"
							classNamePrefix="wfp--react-select"
							isMulti
							isClearable
							options={[]}
							value={bcc}
							onChange={v => setBcc(v as SimpleOption[])}
						/>
					</td>
				</tr>
				<tr>
					<th>Subject</th>
					<td>Daily Operational Brief {(ed.name === editions[0].name) ? '[Executive edition] ' : ''}-- {formatDateLong(ed.docSlug)}</td>
				</tr>
				<tr>
					<th>Body</th>
					<td><em>Email body will contain the full DOB content.</em></td>
				</tr>
				<tr>
					<th>When</th>
					<td>
						<Row className="row-middle" style={{height: 40}}>
							<Col>
								<RadioButton
									id="sendNow" name="when"
									labelText="Send immediately" value={1}
									checked={!whenActive}
									onChange={() => setWhenActive(false)}/>
							</Col>
						</Row>
						<Row className="row-middle" style={{height: 40}}>
							<Col>
								<RadioButton
									id="sendLater" name="when"
									labelText="Schedule send" value={2}
									checked={whenActive}
									onChange={() => setWhenActive(true)}/>
							</Col>
							<Col>
								&nbsp;at&nbsp;
							</Col>
							<Col style={{width: 100}}>
								<ReactSelect
									id="send-when-hour"
									className="wfp--react-select-container"
									classNamePrefix="wfp--react-select"
									menuPlacement="top"
									value={whenHourOptions.find(o => o.value === whenHour)}
									options={whenHourOptions}
									isDisabled={!whenActive}
									onChange={(opt: any) => setWhenHour(opt.value)}
								/>
							</Col>
							<Col>
								&nbsp;
							</Col>
							<Col style={{width: 100}}>
								<ReactSelect
									id="send-when-minute"
									className="wfp--react-select-container"
									classNamePrefix="wfp--react-select"
									menuPlacement="top"
									value={whenMinutesOptions.find(o => o.value === whenMinutes)}
									options={whenMinutesOptions}
									isDisabled={!whenActive}
									onChange={(opt: any) => setWhenMinutes(opt.value)}
								/>
							</Col>
							<Col>
								&nbsp;Rome HQ time on {formatDateLong(ed.docSlug)}
							</Col>
						</Row>
					</td>
				</tr>
			</tbody></table>

			{!errors.length ? null :
				<Blockquote kind="error" className="mt2">
					{errors.map((e, i) => <p key={i}>{e}</p>)}
				</Blockquote>
			}

		</Modal>
	);
}